import { motion } from "framer-motion";

// Subtle luxury atmosphere layered under the hero:
// - Golden light beams
// - Slow floating gold particles
// - Atmospheric haze
export const HeroAtmosphere = () => {
  // Deterministic particle positions for SSR-safety
  const particles = [
    { x: 8, y: 22, s: 2, d: 0 },
    { x: 14, y: 68, s: 1.4, d: 1.6 },
    { x: 22, y: 40, s: 2.2, d: 3.1 },
    { x: 31, y: 82, s: 1.2, d: 0.9 },
    { x: 38, y: 30, s: 2, d: 2.3 },
    { x: 46, y: 74, s: 1.6, d: 4.2 },
    { x: 55, y: 18, s: 2.4, d: 1.2 },
    { x: 62, y: 60, s: 1.4, d: 3.7 },
    { x: 71, y: 88, s: 2, d: 0.4 },
    { x: 79, y: 32, s: 1.8, d: 2.9 },
    { x: 86, y: 66, s: 2.6, d: 1.9 },
    { x: 93, y: 22, s: 1.4, d: 4.7 },
    { x: 95, y: 76, s: 2, d: 3.4 },
    { x: 4, y: 52, s: 1.8, d: 2.1 },
    { x: 48, y: 92, s: 1.4, d: 5.1 },
  ];

  return (
    <>
      {/* Warm sunset glow — bottom left */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          backgroundImage:
            "radial-gradient(circle 900px at 10% 100%, rgba(201,169,97,0.20), transparent 65%)",
        }}
      />
      {/* Cool depth glow — top right */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-70"
        style={{
          backgroundImage:
            "radial-gradient(circle 700px at 90% 10%, rgba(201,169,97,0.10), transparent 60%)",
        }}
      />

      {/* Diagonal light beams */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 overflow-hidden"
        style={{ mixBlendMode: "screen" }}
      >
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: [0, 0.35, 0.15, 0.35] }}
          transition={{
            duration: 14,
            ease: "easeInOut",
            repeat: Infinity,
            delay: 2,
          }}
          className="absolute -top-1/3 -right-1/4 h-[160%] w-[42%] rotate-[18deg]"
          style={{
            background:
              "linear-gradient(90deg, transparent 0%, rgba(229,204,148,0.14) 40%, rgba(229,204,148,0.28) 50%, rgba(229,204,148,0.14) 60%, transparent 100%)",
            filter: "blur(30px)",
          }}
        />
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: [0, 0.22, 0.06, 0.22] }}
          transition={{
            duration: 18,
            ease: "easeInOut",
            repeat: Infinity,
            delay: 4,
          }}
          className="absolute -bottom-1/3 -left-1/4 h-[160%] w-[36%] rotate-[24deg]"
          style={{
            background:
              "linear-gradient(90deg, transparent 0%, rgba(201,169,97,0.12) 40%, rgba(201,169,97,0.24) 50%, rgba(201,169,97,0.12) 60%, transparent 100%)",
            filter: "blur(40px)",
          }}
        />
      </div>

      {/* Atmospheric haze layer */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse 80% 40% at 50% 100%, rgba(20,15,8,0.55), transparent 70%)",
        }}
      />

      {/* Floating gold particles */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        {particles.map((p, i) => (
          <motion.span
            key={i}
            aria-hidden
            initial={{ opacity: 0, y: 0 }}
            animate={{
              opacity: [0, 0.7, 0.4, 0.8, 0],
              y: [0, -30, -60, -100, -140],
              x: [0, i % 2 === 0 ? 12 : -12, 0, i % 2 === 0 ? -8 : 8, 0],
            }}
            transition={{
              duration: 14 + (i % 5) * 2,
              delay: p.d,
              ease: "linear",
              repeat: Infinity,
            }}
            className="absolute rounded-full"
            style={{
              left: `${p.x}%`,
              top: `${p.y}%`,
              width: `${p.s}px`,
              height: `${p.s}px`,
              background:
                "radial-gradient(circle, rgba(229,204,148,0.9) 0%, rgba(201,169,97,0.4) 60%, rgba(201,169,97,0) 100%)",
              boxShadow: "0 0 6px rgba(229,204,148,0.6)",
            }}
          />
        ))}
      </div>
    </>
  );
};
